'use client';

import { faCheckCircle, faCopy, faMobileScreen, faXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useClipboard from '@hooks/useClipboard';
import { Badge, Blockquote, Button, Spinner, Tooltip } from '@ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import QRCode from '~/components/QRCode';
import type { CaptureSession, ScanRequestFailure, ScanRequestResult } from './types';

interface Props {
    session: CaptureSession;
    onRequestScan: () => Promise<ScanRequestResult>;
    onClose: () => void;
}

const FAILURE_KEYS: Record<ScanRequestFailure, string> = {
    no_devices: 'belegAssistant.scanner.noDevices',
    push_failed: 'belegAssistant.scanner.pushFailed',
    device_rejected: 'belegAssistant.scanner.deviceRejected'
};

/**
 * Pairs a phone with this capture session.
 *
 * Sits in the assistant's left rail, stacked: code, the two ways to use it, then what to do with
 * it. The push is the fast path; the QR is not a nicety but the fallback that keeps the feature
 * usable when no device is registered or notifications are off.
 */
const ScannerDialog = ({ session, onRequestScan, onClose }: Props) => {
    const { t } = useTranslation('invoice');
    const { write } = useClipboard();
    const [isSending, setIsSending] = useState(false);
    const [pushResult, setPushResult] = useState<ScanRequestResult | null>(null);

    // Three states, not two: never paired, paired, and paired-then-gone. The last one has to look
    // different from the first, or the code and the steps would read as "nothing happened yet".
    const isConnected = Boolean(session.scanner_connected_at) && !session.scanner_disconnected_at;
    const hasDisconnected = Boolean(session.scanner_connected_at) && Boolean(session.scanner_disconnected_at);

    const handleSend = useCallback(async () => {
        setIsSending(true);
        setPushResult(await onRequestScan());
        setIsSending(false);
    }, [onRequestScan]);

    const steps = [
        t('belegAssistant.scanner.steps.open'),
        t('belegAssistant.scanner.steps.scan'),
        t('belegAssistant.scanner.steps.send')
    ];

    return (
        <div className="tw-flex tw-min-h-full tw-flex-col tw-gap-3 tw-rounded-xl tw-border tw-border-solid tw-border-accents-2 tw-p-3">
            <div className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-flex-1 tw-text-xs tw-font-semibold">{t('belegAssistant.scanner.title')}</span>
                <Tooltip tooltip={t('belegAssistant.actions.close')}>
                    <Button
                        size="xs"
                        color="light"
                        flat
                        onPress={onClose}
                        icon={<FontAwesomeIcon icon={faXmark} />}
                    />
                </Tooltip>
            </div>

            <Badge
                flat
                size="xs"
                color={isConnected ? 'success' : hasDisconnected ? 'warning' : 'light'}
                className="tw-text-center"
            >
                {isConnected && (
                    <>
                        <FontAwesomeIcon icon={faCheckCircle} /> {t('belegAssistant.scanner.connected')}
                    </>
                )}
                {hasDisconnected && t('belegAssistant.scanner.disconnected')}
                {!isConnected && !hasDisconnected && t('belegAssistant.scanner.waiting')}
            </Badge>

            {/* Square and full width of the rail — the code is the thing a phone has to hit. */}
            <div className="tw-relative tw-aspect-square tw-w-full tw-rounded-lg tw-border tw-border-solid tw-border-accents-2 tw-bg-white">
                <QRCode containerClassName="tw-absolute tw-inset-2" value={session.qr_payload} />
            </div>

            <div className="tw-flex tw-flex-col tw-gap-2">
                <Button
                    block
                    size="sm"
                    color="primary"
                    isLoading={isSending}
                    onPress={handleSend}
                    icon={<FontAwesomeIcon icon={faMobileScreen} />}
                >
                    {t('belegAssistant.scanner.sendPush')}
                </Button>
                <Button
                    block
                    size="sm"
                    color="light"
                    onPress={() => write(session.qr_payload)}
                    icon={<FontAwesomeIcon icon={faCopy} />}
                >
                    {t('belegAssistant.scanner.copyLink')}
                </Button>
            </div>

            {!isConnected && pushResult?.sent && (
                <span className="tw-text-xs tw-text-accents-7">
                    {t('belegAssistant.scanner.pushSent', { count: pushResult.devices })}
                </span>
            )}

            {!isConnected && pushResult && !pushResult.sent && (
                <Blockquote color="warning" size="sm">
                    {t(FAILURE_KEYS[pushResult.reason ?? 'push_failed'])}
                </Blockquote>
            )}

            {isConnected && !session.closed_at && (
                <div className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-accents-7">
                    <Spinner size="xs" /> {t('belegAssistant.scanner.awaitingDocuments')}
                </div>
            )}

            {hasDisconnected && (
                <Blockquote color="warning" size="sm">
                    {t('belegAssistant.scanner.disconnectedHint')}
                </Blockquote>
            )}

            {!isConnected && !hasDisconnected && (
                <ol className="tw-m-0 tw-mt-auto tw-flex tw-shrink-0 tw-list-none tw-flex-col tw-gap-1.5 tw-p-0 tw-pt-2">
                    {steps.map((step, index) => (
                        <li key={step} className="tw-flex tw-items-start tw-gap-2 tw-text-xs tw-text-accents-7">
                            <span className="tw-flex tw-h-4 tw-w-4 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-full tw-bg-accents-2 tw-text-[10px] tw-font-semibold tw-text-accents-8">
                                {index + 1}
                            </span>
                            {step}
                        </li>
                    ))}
                </ol>
            )}
        </div>
    );
};

export default ScannerDialog;
